/**
 * 需要想定(日次編集) ページ共通レイアウト。
 * SideMenuLayoutContentLayout + パンくず (需要想定一覧 (一覧ページへのリンク) >
 * {ワークフローID} (ケース詳細へのリンク) > {断面}計画 (current, bold)) を 1 箇所に
 * 集約し、loading / 各エラー画面 / 本体で重複していたヘッダ boilerplate を排除する。
 *
 * [design 差異] design/demand/forecast-today.html L675-681 は遷移元 query で親を切り替え、
 * ワークフロー詳細からの確定前作業では「業務ワークフロー」を親にする。dev はこれを採らず
 * **常に「需要想定一覧」で統一**する (Des 裁定 2026-08-14、issue #5492)。
 * サイドメニューの点灯は #5415 で pathname ベースの静的写像 (= 需要想定一覧) になっており、
 * パンくず先頭だけ「業務ワークフロー」のままだと **nav とパンくずが別の親を宣言する**
 * 状態になるため、パンくず側を静的写像に合わせた。中間クラム (ワークフローID →
 * ケース詳細リンク) は残すのでケース文脈は失われない。
 */
import { useTranslation } from 'react-i18next'

import { PageLayout } from '~/components/common/PageLayout'
import type { SectionSegment } from '~/pages/DemandForecastDaily/models/types'
import { routePaths } from '~/routes/routePaths'

type Props = {
  children: React.ReactNode
  /**
   * ケース固有 (caseId / 断面) のパンくずを表示するか。caseId 解決前の
   * loading 状態では daily ルートが未確定なので false にし、「需要想定一覧」
   * (一覧ページへのリンク) のみ表示する。
   */
  showDailyCrumb?: boolean
  /**
   * page-header 右端に表示する追加要素 (design `data-page-header-extras` 相当。
   * 例: GC カウントダウン, design L437-446)。
   */
  headerExtra?: React.ReactNode
  /** ワークフローケース UUID。中間クラムの表示と SectionHeader リンクに使う。 */
  caseId?: string
  /**
   * human-readable なワークフロー業務キー (例: `D0-20260729-27D5`)。中間クラムの
   * 表示ラベルに使う (SectionHeader 側のリンク先は caseId (UUID)、ADR
   * workflow-human-readable-id-separate-from-pk)。fetch 前で未解決のときは caseId に
   * フォールバックする。
   */
  workflowId?: string
  /**
   * 計画断面 (対象日と今日 (JST) の差で判定、AC-DFD-000)。leaf クラム「{断面}計画」の
   * 文言決定に使う。未定義 (ケース読込前・取得失敗・対象日が過去日/明後日より先) の間は
   * leaf クラムを出さない。
   */
  sectionSegment?: SectionSegment
  /** ページ最上部の通知 (`PageLayout` の `notices`。枠の端に寄せる)。 */
  notices?: React.ReactNode
}

const sectionSegmentToPlanCrumbKey: Record<SectionSegment, string> = {
  today: 'page:demandForecastDaily.breadcrumb.planToday',
  tomorrow: 'page:demandForecastDaily.breadcrumb.planTomorrow',
  dayaftertomorrow: 'page:demandForecastDaily.breadcrumb.planDayAfterTomorrow',
}

export const DemandForecastDailyLayout = ({
  children,
  showDailyCrumb = true,
  headerExtra,
  caseId,
  workflowId,
  sectionSegment,
  notices,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <PageLayout
      breadcrumbItems={[
        {
          label: t('common:route.demandForecastList'),
          to: routePaths.demandForecastList.path,
        },
        ...(showDailyCrumb && caseId !== undefined
          ? [
              {
                label:
                  workflowId !== undefined && workflowId !== ''
                    ? workflowId
                    : caseId,
                // 中間クラム = 一階層上の画面 (WorkflowCaseDetail) なのでリンクにする。
                // 旧実装は「SectionHeader に同じ導線があるため」テキストのみにしていたが、
                // パンくずは「今どこにいて、どこへ戻れるか」を示す部品であり、
                // 別の場所に導線があることは中間クラムを不活性にする理由にならない
                // (デザイナー指摘 2026-08-12「下層画面を開いている際、上部の画面名が
                // クリックできるべき」)。 SectionHeader 側の導線はそのまま残す。
                to: routePaths.workflowCaseDetail.path.replace(
                  ':caseId',
                  caseId,
                ),
              },
              ...(sectionSegment !== undefined
                ? [
                    {
                      label: t(sectionSegmentToPlanCrumbKey[sectionSegment]),
                      bold: true,
                    },
                  ]
                : []),
            ]
          : []),
      ]}
      headerExtra={headerExtra}
      notices={notices}
    >
      {children}
    </PageLayout>
  )
}
